import { zodResolver } from '@hookform/resolvers/zod'
import { CheckIcon, PencilSimpleIcon } from '@phosphor-icons/react'
import { useMemo, useState } from 'react'
import { FormProvider, Resolver, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { useUpdateGuestPass } from '@/api/clients/update-guest-pass'
import { NumericInputFormField } from '@/components/form/numeric-input-form-field'
import { Button } from '@/components/ui/button'
import { Icon } from '@/components/ui/icon'
import { Skeleton } from '@/components/ui/skeleton'
import { Typography } from '@/components/ui/typography'
import { toastFactory } from '@/factories'
import { useFormSave } from '@/hooks'
import { getApiErrorMessage } from '@/utils/error'

import { makeGuestPassesSchema } from './guest-passes.constants'
import * as S from './guest-passes.styled'
import { GuestPassesAllocationProps, GuestPassesFormValues } from './guest-passes.types'

export const GuestPassesAllocation = ({
  clientId,
  totalPasses,
  usedPasses,
  isLoading,
}: GuestPassesAllocationProps) => {
  const { t } = useTranslation(['clients', 'common'])
  const [isEditing, setIsEditing] = useState(false)
  const { mutateAsync, isPending } = useUpdateGuestPass(clientId)

  const schema = useMemo(
    () =>
      makeGuestPassesSchema(t).refine((val) => val.totalPasses >= usedPasses, {
        message: t('clients:guestPasses.totalPasses.validation.minUsed', { usedPasses }),
        path: ['totalPasses'],
      }),
    [t, usedPasses],
  )

  const form = useForm<GuestPassesFormValues>({
    mode: 'onSubmit',
    resolver: zodResolver(schema) as Resolver<GuestPassesFormValues>,
    reValidateMode: 'onBlur',
    values: { totalPasses },
  })

  const handleCancel = () => {
    form.reset()
    setIsEditing(false)
  }

  const handleSave = useFormSave({
    form,
    onInvalid: () => {
      form.setValue('totalPasses', form.getValues('totalPasses'), {
        shouldTouch: true,
        shouldValidate: true,
      })
    },
    onSubmit: async (values) => {
      try {
        await mutateAsync({ totalPasses: values.totalPasses })
        toastFactory.success(t('clients:guestPasses.toasts.updateSuccess'))
        setIsEditing(false)
      } catch (error) {
        toastFactory.error(getApiErrorMessage(error))
      }
    },
    onUnchanged: () => setIsEditing(false),
  })

  return (
    <FormProvider {...form}>
      <S.AllocationCard>
        <S.SectionHeader>
          <S.SectionTitleGroup>
            <Typography as="h2" size="heading-small" color="black800">
              {t('clients:guestPasses.title')}
            </Typography>
            <Typography as="span" size="text-medium" color="gray600">
              {t('clients:guestPasses.usedCount', { total: totalPasses, used: usedPasses })}
            </Typography>
          </S.SectionTitleGroup>
          <S.HeaderActions>
            {isEditing ? (
              <>
                <Button size="small" use="secondary" onClick={handleCancel}>
                  {t('common:actions.cancel')}
                </Button>
                <Button size="small" loading={isPending} onClick={handleSave}>
                  {t('common:actions.saveChanges')}
                  <Icon icon={CheckIcon} size="1.6rem" />
                </Button>
              </>
            ) : (
              <Button size="small" disabled={isLoading} onClick={() => setIsEditing(true)}>
                {t('common:actions.edit')}
                <Icon icon={PencilSimpleIcon} size="1.6rem" />
              </Button>
            )}
          </S.HeaderActions>
        </S.SectionHeader>

        <S.FieldsTable>
          {isLoading ? (
            <S.FieldRow>
              <Skeleton variant="text" width="16rem" height="1.4rem" />
              <Skeleton variant="text" width="4rem" height="1.4rem" />
            </S.FieldRow>
          ) : (
            <S.FieldRow>
              <Typography as="span" size="text-medium" color="primary500">
                {t('clients:guestPasses.totalPasses.label')}
              </Typography>
              {isEditing ? (
                <NumericInputFormField
                  clearable
                  size="small"
                  name="totalPasses"
                  disabled={isPending}
                  onRestore={() => form.resetField('totalPasses')}
                />
              ) : (
                <S.FieldValue>{totalPasses}</S.FieldValue>
              )}
            </S.FieldRow>
          )}
        </S.FieldsTable>
      </S.AllocationCard>
    </FormProvider>
  )
}
